import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { listMyAssignedKawasans, listUnitsByKawasan } from "../lib/api";

const List = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setError("");

      const assigned = await listMyAssignedKawasans();
      const areas = [...new Map(
        (Array.isArray(assigned) ? assigned : [])
          .map((item) => {
            const kodKawasan = String(item?.kod_kawasan || item?.kodKawasan || "").trim();
            const kawasan = String(item?.kawasan || item?.kawasan_name || "").trim();
            return [kodKawasan || kawasan, { kodKawasan, kawasan }];
          })
          .filter(([key]) => key)
      ).values()];

      const groups = await Promise.all(
        areas.map((area) => listUnitsByKawasan(area))
      );

      const seen = new Set();
      const units = groups.flat().filter((unit) => {
        const key = String(unit?.no_akaun || unit?.noAkaun || unit?.id || "");
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      units.sort((a, b) => {
        const areaCompare = String(a.kawasan || "").localeCompare(String(b.kawasan || ""));
        if (areaCompare) return areaCompare;
        return String(a.nombor_unit || a.nomborUnit || "").localeCompare(
          String(b.nombor_unit || b.nomborUnit || "")
        );
      });

      setRows(units);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Gagal memuatkan unit yang ditugaskan.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) =>
      [row.no_akaun, row.noAkaun, row.bil, row.nombor_unit, row.nomborUnit, row.kod_kawasan, row.kawasan]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [rows, search]);

  const openUnit = (row) => {
    const noAkaun = row.no_akaun || row.noAkaun;
    if (!noAkaun) return;
    navigate("/renter-detail", { state: { qrResult: String(noAkaun) } });
  };

  return (
    <MainLayout>
      <div className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Unit Ditugaskan</h2>
            <p className="text-xs text-gray-500 mt-1">
              Senarai unit aktif di bawah tugasan ejen.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-50"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Cari no akaun, BIL, unit atau kawasan"
          className="mt-4 border rounded-lg w-full p-2 text-sm"
        />

        {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}

        <div className="mt-4 space-y-2">
          {!loading && filteredRows.length === 0 ? (
            <p className="text-sm text-gray-500">Tiada unit ditugaskan.</p>
          ) : (
            filteredRows.map((row) => (
              <button
                key={row.id || row.no_akaun || row.noAkaun}
                onClick={() => openUnit(row)}
                className="w-full text-left rounded-lg border bg-white p-3 hover:bg-blue-50"
              >
                <div className="font-semibold text-gray-800">
                  No Akaun: {row.no_akaun || row.noAkaun || "-"}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Unit: {row.nombor_unit || row.nomborUnit || "-"}
                </div>
                <div className="text-sm text-gray-600">
                  Kawasan: {row.kod_kawasan ? `${row.kod_kawasan} — ` : ""}{row.kawasan || "-"}
                </div>
                {row.bil ? <div className="text-xs text-gray-500 mt-1">BIL: {row.bil}</div> : null}
              </button>
            ))
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default List;
